import React, { forwardRef } from 'react'

import { CustomDropdown, DropdownItem } from '@/app/components/CustomDropdown'
import {
  errorClasses,
  getErrorInputClasses,
  helperClasses,
  labelClasses,
} from '@/app/components/Form/styles'
import { cn } from '@/app/utils/style'

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  helperText?: string
  variant?: 'default' | 'small'
  labelClassName?: string
  items?: DropdownItem[]
  onValueChange?: (value: string) => void
  placeholder?: string
  portal?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      id,
      required,
      variant = 'default',
      labelClassName,
      items,
      onValueChange,
      value,
      placeholder,
      portal,
      children,
      ...props
    },
    ref,
  ) => {
    const inputId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`
    const sizeClass = variant === 'small' ? 'input-smallLight' : 'input-defaultLight'

    if (items && onValueChange) {
      return (
        <div className="flex flex-col flex-1">
          {label && (
            <label htmlFor={inputId} className={cn(labelClasses, labelClassName)}>
              {label}
            </label>
          )}
          <CustomDropdown
            items={items}
            value={value as string}
            placeholder={placeholder}
            onChange={onValueChange}
            variant={variant}
            portal={portal}
          />
          {error && (
            <span id={`${inputId}-error`} className={errorClasses}>
              {error}
            </span>
          )}
          {!error && helperText && (
            <span id={`${inputId}-helper`} className={helperClasses}>
              {helperText}
            </span>
          )}
        </div>
      )
    }

    return (
      <div className="flex flex-col flex-1">
        {label && (
          <label htmlFor={inputId} className={cn(labelClasses, labelClassName)}>
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={inputId}
          className={cn(sizeClass, getErrorInputClasses(error), className)}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
          }
          required={required}
          {...props}
        >
          {children}
        </select>
        {error && (
          <span id={`${inputId}-error`} className={errorClasses}>
            {error}
          </span>
        )}
        {!error && helperText && (
          <span id={`${inputId}-helper`} className={helperClasses}>
            {helperText}
          </span>
        )}
      </div>
    )
  },
)

Select.displayName = 'Select'
